import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { newsService } from '../../services';

// Async thunks
export const fetchNews = createAsyncThunk(
  'news/fetchNews',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data, error } = await newsService.getNews(filters);
      if (error) throw new Error(error);
      return data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchNewsById = createAsyncThunk(
  'news/fetchNewsById',
  async (newsId, { rejectWithValue }) => {
    try {
      const { data, error } = await newsService.getNewsById(newsId);
      if (error) throw new Error(error);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createNews = createAsyncThunk(
  'news/createNews',
  async (newsData, { rejectWithValue }) => {
    try {
      const { data, error } = await newsService.createNews(newsData);
      if (error) throw new Error(error);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleNewsLike = createAsyncThunk(
  'news/toggleNewsLike',
  async ({ userId, newsId }, { rejectWithValue }) => {
    try {
      const { liked, error } = await newsService.toggleNewsLike(userId, newsId);
      if (error) throw new Error(error);
      return { newsId, liked, userId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addNewsComment = createAsyncThunk(
  'news/addNewsComment',
  async ({ userId, newsId, content }, { rejectWithValue }) => {
    try {
      const { data, error } = await newsService.addNewsComment(userId, newsId, content);
      if (error) throw new Error(error);
      return { newsId, comment: data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  items: [],
  selected_news: null,
  loading: false,
  selected_loading: false,
  error: null,
  categories: ['feature', 'community', 'event', 'update'] // From DB enum
};

export const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedNews: (state) => {
      state.selected_news = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch news
      .addCase(fetchNews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNews.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch news by ID
      .addCase(fetchNewsById.pending, (state) => {
        state.selected_loading = true;
        state.error = null;
      })
      .addCase(fetchNewsById.fulfilled, (state, action) => {
        state.selected_loading = false;
        state.selected_news = action.payload;
      })
      .addCase(fetchNewsById.rejected, (state, action) => {
        state.selected_loading = false;
        state.error = action.payload;
      })
      
      // Create news
      .addCase(createNews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNews.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload); // Add to beginning
      })
      .addCase(createNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Toggle news like
      .addCase(toggleNewsLike.fulfilled, (state, action) => {
        const { newsId, liked, userId } = action.payload;
        
        // Update in items list
        const newsItem = state.items.find(item => item.id === newsId);
        if (newsItem) {
          if (liked) {
            // Add user like
            newsItem.news_likes = newsItem.news_likes ? [...newsItem.news_likes, { user_id: userId }] : [{ user_id: userId }];
          } else {
            // Remove user like
            newsItem.news_likes = newsItem.news_likes ? newsItem.news_likes.filter(like => like.user_id !== userId) : [];
          }
        }
        
        // Update in selected news
        if (state.selected_news && state.selected_news.id === newsId) {
          if (liked) {
            state.selected_news.news_likes = state.selected_news.news_likes ? [...state.selected_news.news_likes, { user_id: userId }] : [{ user_id: userId }];
          } else {
            state.selected_news.news_likes = state.selected_news.news_likes ? state.selected_news.news_likes.filter(like => like.user_id !== userId) : [];
          }
        }
      })
      .addCase(toggleNewsLike.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // Add news comment
      .addCase(addNewsComment.fulfilled, (state, action) => {
        const { newsId, comment } = action.payload;
        
        // Update selected news comments
        if (state.selected_news && state.selected_news.id === newsId) {
          if (!state.selected_news.news_comments) {
            state.selected_news.news_comments = [];
          }
          state.selected_news.news_comments.push(comment);
        }
        
        // Update comment count in items list
        const newsItem = state.items.find(item => item.id === newsId);
        if (newsItem) {
          const currentComments = newsItem.news_comments ? newsItem.news_comments.length : 0;
          newsItem.news_comments = newsItem.news_comments ? [...newsItem.news_comments, { id: 'temp' }] : [{ id: 'temp' }];
        }
      })
      .addCase(addNewsComment.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSelectedNews } = newsSlice.actions;
export default newsSlice.reducer;